<script>
  import dayjs from "dayjs";
  import ChartContainer from "../../../components/chart/ChartContainer.svelte";
  import TableArea from "../../../components/chart/TableArea.svelte";
  import { usePageContext } from "../../../components/pages/pageContext.js";
  import { useProject } from "../../../hooks/useProject.js";
  import { getContext, onMount } from "svelte";
  import { fetchPSIItemData } from "../../../utils/getData.js";
  import { getValueWrapper } from "../../MPS/PSITable/utils.js";
  import GanttHelper from "../../../components/ganttchart/GanttHelper.js";
  import { writable } from "svelte/store";
  import PSITableUpperPane from "./PSITableUpperPane.svelte";
  import { aggregatePSITable } from "../../MPS/PSITable/aggregate.js";
  import { attachConfigurationStorageToPSITableStore, initPSITableStore } from "../../MPS/PSITable/PSITableStore.js";
  import { getMPSConfigurationStorage } from "../../../utils/configurationStorage/views/MPSConfigurationStorage.js";
  import PSITableGroupedRows from "./PSITableGroupedRows.svelte";
  import ChartArea from "../../../components/chart/ChartArea.svelte";
  import MPSCalendarHeader from "../../MPS/MPSCalendarHeader.svelte";
  import ChartContent from "../../../components/chart/ChartContent.svelte";
  import PSITableChartContentRow from "./PSITableChartContentRow.svelte";
  import PSITableUngroupedRows from "./PSITableUngroupedRows.svelte";

  export let user
  export let projectId
  export let data

  let viewportWidth = writable(window.innerWidth);
  let viewportHeight = writable(window.innerHeight);

  const {handleFetchErrors} = getContext("fetchErrorHandler")
  const myScheduleProjectData = useProject(projectId, handleFetchErrors)
  const pageContext = usePageContext()
  $: pageContext.setHeaderProps({
    title: $myScheduleProjectData ? `${$myScheduleProjectData.name} - PSI表` : `PSI表`,
  })

  let DataByRowCache = null
  async function RowDataFetcher(rowData) {
    if (DataByRowCache && $DataByRowCache[rowData.itemCode]) return $DataByRowCache[rowData.itemCode]
    const data = await fetchPSIItemData(projectId, rowData.itemCode)
    const calculated = aggregatePSITable("divideByNextWeekAverage", null, data)
    data.computed = {
      "divideByNextWeekAverage": calculated
    }
    DataByRowCache.update(prev => ({...prev, [rowData.itemCode]: data}))
    return data
  }

  const store = initPSITableStore({
    AggregateMethod: "divideByNextWeekAverage",
    CurrentDisplayedStartDate: new Date(data.Dates[0]),
    DisplayedEndDate: new Date(data.Dates.at(-1)),
    DisplayedRowCount: 20,
    DisplayedSpanInSeconds: 604800 * 6,
    DisplayedStartDate: new Date(data.Dates[0]),
    RowDataFetcher,
    Rows: [],
    TableAreaColumnTitle: "",
    RowsDisplayedStart: 0,
    ValidStartDate: new Date(data.Dates[0]),
    ValidEndDate: new Date(data.Dates.at(-1)),
  })

  const {
    DisplayedSpanInSeconds,
    Rows,
    RowsCached,
    CurrentDisplayedStartDate,
    ValidEndDate,
    CurrentDisplayedEndDate,
    ValidStartDate,
    TableAreaWidth,
  } = store
  DataByRowCache = RowsCached

  $: Rows.set(data.rows.map(row => {
    return {
      originalData: row,
      getValue: (_, dateString) => getValueWrapper(() => row[dateString], "")
    }
  }))

  TableAreaWidth.set(data.FixedColumns.length * 80)

  const ScrollTime = (e) => {
    e.preventDefault();
    if (e.ctrlKey) {
      const currentDisplayedSpanInSeconds = $DisplayedSpanInSeconds
      let newDisplayedSpanInSeconds
      // Zoom
      if (e.deltaY > 0) {
        // If we want a more strict restriction use the following code. Currently, I think it's unnecessary
        // GanttStore.DisplayedSpanInSeconds.update((d) => Math.min(d * 1.05, GanttHelper.GHYEAR, (validEndDate.getTime() - validStartDate.getTime()) / 1000));
        newDisplayedSpanInSeconds = Math.min(currentDisplayedSpanInSeconds * 1.05, GanttHelper.GHYEAR)
      } else if (e.deltaY < 0) {
        newDisplayedSpanInSeconds = Math.max(currentDisplayedSpanInSeconds / 1.05, GanttHelper.GHDAY)
      }
      DisplayedSpanInSeconds.set(newDisplayedSpanInSeconds)
    }
    else {
      // Move
      let newCurrentDisplayedStartDate
      if (e.deltaY > 0) {
        if (isNaN($ValidEndDate.getTime())) {
          newCurrentDisplayedStartDate = new Date($CurrentDisplayedStartDate.getTime() + ($CurrentDisplayedEndDate.getTime() - $CurrentDisplayedStartDate.getTime()) / 48);
        } else {
          newCurrentDisplayedStartDate = new Date(Math.min($ValidEndDate.getTime() - $DisplayedSpanInSeconds * 1000, $CurrentDisplayedStartDate.getTime() + 1000 * 60 * 60 * 24));
        }
        if (newCurrentDisplayedStartDate < $CurrentDisplayedStartDate) {
          newCurrentDisplayedStartDate = $CurrentDisplayedStartDate;
        }
      }
      else if (e.deltaY < 0) {
        if (isNaN($ValidStartDate.getTime())) {
          newCurrentDisplayedStartDate = new Date($CurrentDisplayedStartDate.getTime() - ($CurrentDisplayedEndDate.getTime() - $CurrentDisplayedStartDate.getTime()) / 48);
        } else {
          newCurrentDisplayedStartDate = new Date(Math.max($ValidStartDate.getTime(), $CurrentDisplayedStartDate.getTime() - 1000 * 60 * 60 * 24))
        }
        if (newCurrentDisplayedStartDate > $CurrentDisplayedStartDate) {
          newCurrentDisplayedStartDate = $CurrentDisplayedStartDate;
        }
      }
      CurrentDisplayedStartDate.set(newCurrentDisplayedStartDate)
    }
  }

  function handleResize() {
    viewportWidth.set(window.innerWidth);
    viewportHeight.set(window.innerHeight);
  }

  onMount(async () => {
    const configurationStorage = getMPSConfigurationStorage(user.id, projectId)
    await attachConfigurationStorageToPSITableStore(store, configurationStorage)

    // Add resize event listener
    window.addEventListener("resize", handleResize);
  });
</script>

<div class="root">
  <div class="main-psi-table">
    <PSITableUpperPane store={store} />
    <ChartContainer store={store}>
      <svelte:fragment slot="table-area">
        {#each data.FixedColumns as fixedColumn}
          {#if fixedColumn === "合計"}
            <div>
              <TableArea store={store} headerTitleString={fixedColumn}>
                <PSITableUngroupedRows
                  slot="rows"
                  columnTitle={fixedColumn}
                  store={store}
                />
              </TableArea>
            </div>
          {:else}
            <div>
              <TableArea store={store} headerTitleString={fixedColumn}>
                <PSITableGroupedRows
                  slot="rows"
                  columnTitle={fixedColumn}
                  store={store}
                />
              </TableArea>
            </div>
          {/if}
        {/each}
      </svelte:fragment>
      <ChartArea store={store} slot="chart-area">
        <MPSCalendarHeader store={store} ScrollTime={ScrollTime} />
        <ChartContent store={store} let:props>
          <PSITableChartContentRow
            slot="chart-content-row"
            store={store}
            props={{
              ...props,
              dateStrings: data.Dates,
              dates: data.Dates.map(dateString => dayjs(dateString).toDate()),
            }}
          />
        </ChartContent>
      </ChartArea>
    </ChartContainer>
  </div>
</div>

<style>
  .root {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
  }

  .main-psi-table {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
  }

  .main-psi-table {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    flex: 1;
  }
</style>